import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Tab, Tabs } from '@mui/material';
import { Grade } from '@mui/icons-material';
import { useItem, useUpdateItemDescription } from '@/hooks/item';
import { UpdateItemDescriptionModal } from '@/types/modal';
import { Textarea } from '@/components/molecules/common/Form';
import { ErrorModal, StandardModal, SuccessModal } from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';

const StyledTab = styled(Tab)(() => ({
  height: '2rem',
  minHeight: '2rem',
}));

const StyledTabs = styled(Tabs)(() => ({
  '& .MuiTabs-indicator': {
    bottom: '0.5rem',
  },
  '& .MuiTabs-scrollButtons': {
    height: 40,
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  },
}));

const LANGUAGES: Array<string> = ['EN', 'DE', 'ES', 'FR', 'IT', 'KO', 'HK', 'SV', 'NL'].sort((a, b) =>
  a.localeCompare(b),
);

const View: React.FC<{ modal: UpdateItemDescriptionModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const getDefaultDescription = (language: string): string => {
    let value: string | null | undefined = '';
    switch (language.toLowerCase()) {
      case 'en':
        value = item.descriptionEn;
        break;
      case 'de':
        value = item.descriptionDe;
        break;
      case 'es':
        value = item.descriptionEs;
        break;
      case 'fr':
        value = item.descriptionFr;
        break;
      case 'it':
        value = item.descriptionIt;
        break;
      case 'ko':
        value = item.descriptionKo;
        break;
      case 'nl':
        value = item.descriptionNl;
        break;
      case 'sv':
        value = item.descriptionSv;
        break;
      case 'hk':
        value = item.descriptionHk;
        break;
      default:
        value = description;
        break;
    }

    return value || '';
  };

  const [description, setDescription] = useState(getDefaultDescription(item.language || ''));
  const [tab, setTab] = useState(0);
  const onChangeTab = (_: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
  };
  const tabLabels = useMemo(() => {
    const idx = item.language ? LANGUAGES.indexOf(item.language.toUpperCase()) : 0;

    if (idx < 0) return LANGUAGES;

    const langs = LANGUAGES.filter((i) => i !== LANGUAGES[idx]);

    return [LANGUAGES[idx], ...langs];
  }, [item.language]);

  const itemR = useItem(item.productId, false);
  const submitR = useUpdateItemDescription(item.productId);

  useEffect(() => {
    if (item.language) {
      const idx = tabLabels.indexOf(item.language.toUpperCase());
      setTab(idx > -1 ? idx : 0);
    }
  }, [tabLabels]);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.update_item_description.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={t('modal.update_item_description.title')}
        onClose={onClose}
      />
    );
  }

  const updatable = tabLabels[tab]?.toLowerCase() === item.language?.toLowerCase();

  const body = (
    <div className={styles.updateItemDescription}>
      <div className={styles.text1rem}>{`${t('modal.update_item_description.item')} #${item.productId}`}</div>
      <div
        data-cy={'language-tabs'}
        className={styles.flex100}
      >
        <StyledTabs
          variant="scrollable"
          value={tab}
          onChange={onChangeTab}
        >
          {tabLabels.map((label) =>
            label.toLowerCase() === item.language?.toLowerCase() ? (
              <StyledTab
                icon={<Grade />}
                iconPosition="start"
                label={label}
                key={label}
                data-cy={`language-tab language-tab-${label}`}
              />
            ) : (
              <StyledTab
                label={label}
                key={label}
                data-cy={`language-tab language-tab-${label}`}
              />
            ),
          )}
        </StyledTabs>
      </div>
      <div className={clsx([styles.text1rem, styles.textGrey])}>
        {updatable ? t('modal.update_item_description.original') : t('modal.update_item_description.google')}
      </div>
      <div className={styles.updateItemDescriptionTextarea}>
        <Textarea
          rows={6}
          value={updatable ? description : getDefaultDescription(tabLabels[tab])}
          onChange={setDescription}
          disabled={!updatable}
        />
      </div>
    </div>
  );

  return (
    <StandardModal
      title={t('modal.update_item_description.title')}
      onClose={onClose}
      onSubmit={() =>
        submitR.mutate({
          description,
          sourceLang: item.language || '',
        })
      }
      submittable={submitR.kind !== 'Loading' && getDefaultDescription(item.language || '') !== description}
      body={body}
      containerStyle={{ maxWidth: 650 }}
      modalName="update_item_description"
    />
  );
};

export default View;
